import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Pencil, Trash2, Users, ListChecks, Sigma, Plane, Save, ListTodo, Mail, CheckCircle2, Circle, Printer, Send, Mailbox, Lock, CalendarCheck, FileText, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { FloatingSaveButton } from "@/components/FloatingSaveButton";
import { DestinationMultiSelect, displayDestination } from "@/components/DestinationMultiSelect";
import {
  TRIP_STATUS_LABELS, TRIP_STATUS_VARIANT, RESERVA_TYPE_LABELS, RESERVA_STATUS_LABELS,
  RESERVA_STATUS_CLASS, RESERVA_TRANSITIONS, PARTNER_LABELS, fmtEUR, fmtUSD, fmtPct, fmtDate, SOURCES,
} from "@/pages/trips/shared";
import { openVoucher } from "@/pages/trips/documents";
import ReservaDetail from "@/pages/trips/ReservaDetail";
import ReservasDayList from "@/pages/trips/ReservasDayList";
import AsistenteTab from "@/pages/trips/AsistenteTab";
import ReconfirmacionesTab from "@/pages/trips/ReconfirmacionesTab";
import TravelDocTab from "@/pages/trips/TravelDocTab";
import KimKimTab from "@/pages/trips/KimKimTab";
import { useAuth } from "@/context/AuthContext";

const SOLD_STATUSES_EXCLUDED = ["cotizaciones", "cancelado", "archivado"];

const isKimKimTrip = (trip) => !!(
  trip?.kimkim_trip_url
  || trip?.kimkim_code
  || trip?.partner === "kimkim"
  || (trip?.source || "").toLowerCase() === "kimkim"
);

// Celda compacta del panel resumen fijo del viaje (bajo el título principal).
// Estructura: label superior en mayúsculas + valor. Alineación tabular para números/fechas.
function SummaryCell({ label, value, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate text-sm tabular-nums">{value ?? "—"}</div>
    </div>
  );
}

// Fecha compacta d/m/yy (año en 2 dígitos) — solo para el panel resumen, para
// que las 4 fechas quepan sin ocupar espacio. El resto de la app usa fmtDate largo.
function fmtDateShort(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getDate()}/${d.getMonth() + 1}/${String(d.getFullYear()).slice(-2)}`;
}

// TabLink · combina Radix TabsTrigger + <a href> nativo para que el navegador
// respete Cmd/Ctrl+click y clic-derecho → «Abrir en pestaña nueva» (2026-08 ·
// multitab). Con click normal (sin modifiers), interceptamos el evento y
// llamamos a setActiveTab para navegar en SPA sin recargar.
function TabLink({ value, testid, icon: Icon, label, tripId, setActiveTab, badge, badgeTestid }) {
  const href = `/app/trips?trip=${tripId}&tab=${value}`;
  return (
    <TabsTrigger value={value} data-testid={testid} asChild>
      <a
        href={href}
        onClick={(e) => {
          // Cmd/Ctrl/Shift/Alt/middle-click → dejamos que el navegador
          // gestione (abrir en pestaña nueva, ventana, etc.).
          if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          setActiveTab(value);
        }}
      >
        {Icon && <Icon className="mr-1.5 h-4 w-4" />}
        {label}
        {badge != null && (
          <span className="ml-1.5 rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground" data-testid={badgeTestid}>{badge}</span>
        )}
      </a>
    </TabsTrigger>
  );
}


export default function TripDetail({ tripId, onBack }) {
  const { user } = useAuth();
  // URL como fuente de verdad para la pestaña activa. Permite:
  //   · Abrir la app con `/app/trips/:id?tab=traveldoc` directamente
  //   · Click-derecho sobre una tab → «Abrir en pestaña nueva»
  //   · Compartir un enlace concreto con el equipo
  // (2026-08 · petición equipo · workflow multitab).
  const [searchParams, setSearchParams] = useSearchParams();
  const TAB_WHITELIST = new Set([
    "datos", "contabilidad", "reservas", "recordatorios",
    "asistente", "reconfirmaciones", "traveldoc", "kimkim",
  ]);
  const tabFromUrl = searchParams.get("tab") || "reservas";
  const activeTab = TAB_WHITELIST.has(tabFromUrl) ? tabFromUrl : "reservas";
  const setActiveTab = (next) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("tab", next);
      // Cambiar de tab cierra la reserva abierta (si la había) — evita
      // que la URL apunte a una reserva desde otra tab.
      if (next !== "reservas") p.delete("reserva");
      return p;
    }, { replace: false });
  };
  const canSeeRentabilidad = !!(
    user?.is_admin
    || user?.ver_rentabilidad
    // Roles con acceso ejecutivo/dirección: dirección operaciones,
    // dirección comercial y socio director también pueden ver rentabilidad
    // de viajes (2026-07, alta explícita de Marina · dir_operaciones).
    || ["dir_operaciones", "director_sales", "socio_director", "dir_admin_general"].includes(user?.role)
  );
  const detailQ = useQuery({ queryKey: ["trip", tripId], queryFn: async () => (await api.get(`/trips/${tripId}`)).data });
  const data = detailQ.data;

  if (detailQ.isLoading || !data) {
    return <div className="space-y-3"><Skeleton className="h-9 w-48" /><Skeleton className="h-64 w-full" /></div>;
  }
  const { trip, travelers, reservas, recordatorios, accounting } = data;
  const pendientes = (recordatorios || []).filter((r) => !r.solved).length;
  const sold = !SOLD_STATUSES_EXCLUDED.includes(trip.status);
  const kk = isKimKimTrip(trip);

  // Teléfono mostrado en el resumen: viene del VIAJERO PRINCIPAL (el marcado con
  // `client=true`, o el que coincide con `trip.main_traveler` por nombre). Fallback:
  // `trip.phone` legacy. Al editar el viajero principal, el resumen se actualiza solo.
  const mainTraveler = (travelers || []).find((t) => t.client)
    || (travelers || []).find((t) => `${t.first_name || ""} ${t.last_name || ""}`.trim() === (trip.main_traveler || "").trim())
    || null;
  const summaryPhone = mainTraveler?.phone || trip.phone || "—";
  // `titular_name` (2026-07 · Eduardo): fuente de verdad para Travel Docs y
  // templates cliente-facing. `trip.main_traveler` es el título del itinerario
  // (puede tener "v5 - booked", "2.0", notas…) — para el cliente y las
  // plantillas siempre usamos el nombre real del viajero principal.
  const titularName = mainTraveler
    ? `${(mainTraveler.first_name || "").trim()} ${(mainTraveler.last_name || "").trim()}`.trim() || (trip.main_traveler || "")
    : (trip.main_traveler || "");
  const tripWithTitular = { ...trip, titular_name: titularName };

  return (
    <div className="space-y-5" data-testid="trip-detail">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={onBack} data-testid="trip-back"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <p className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
              <Plane className="h-6 w-6 text-primary" /> {trip.main_traveler}
            </p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant={TRIP_STATUS_VARIANT[trip.status] || "secondary"} className="text-[10px]">{TRIP_STATUS_LABELS[trip.status] || trip.status}</Badge>
              {displayDestination(trip.destination) || "—"} · {trip.source || "—"} · {PARTNER_LABELS[trip.partner] || trip.partner}
            </div>
          </div>
        </div>
      </div>

      {/* Panel resumen fijo del viaje · siempre visible bajo el título.
          Elimina la redundancia con el sub-header de cada reserva.
          Grid con anchos proporcionales: Tel. más ancho (números largos),
          Ad/Ch/Support más estrechos (valores cortos). */}
      <Card data-testid="trip-summary-panel">
        <CardContent className="grid grid-cols-2 gap-4 p-4 sm:[grid-template-columns:minmax(0,2fr)_minmax(0,2fr)_minmax(0,0.7fr)_minmax(0,1.1fr)_minmax(0,2.4fr)_minmax(0,1.8fr)_minmax(0,1.8fr)_minmax(0,1fr)]">
          <SummaryCell label="Start" value={fmtDateShort(trip.start_date)} testid="trip-summary-start" />
          <SummaryCell label="Finish" value={fmtDateShort(trip.end_date)} testid="trip-summary-end" />
          <SummaryCell label="Ad" value={trip.adults ?? trip.number_of_travelers ?? "—"} testid="trip-summary-ad" />
          <SummaryCell
            label="Ch"
            value={
              <>
                {trip.number_of_children ?? 0}
                {trip.number_of_children > 0 && trip.children_ages && (
                  <span className="ml-1 text-[11px] text-muted-foreground">({trip.children_ages})</span>
                )}
              </>
            }
            testid="trip-summary-ch"
          />
          <SummaryCell label="Tel." value={summaryPhone} testid="trip-summary-phone" />
          <SummaryCell label="Ag. Ventas" value={trip.agent || "—"} testid="trip-summary-agent" />
          <SummaryCell label="Ag. Reservas" value={trip.ag_operaciones || "—"} testid="trip-summary-ops" />
          <SummaryCell label="Support" value={trip.traveler_support || "—"} testid="trip-summary-support" />
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Scroll horizontal en móvil: sin él, los últimos tabs quedan fuera de pantalla
        (Reservas/Reconfirmaciones/Travel Document/KimKim). `min-w-max` fuerza a la lista
        a mantener su ancho natural para que el overflow-x active el scroll horizontal. */}
        <div className="-mx-1 overflow-x-auto pb-1 [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30">
          <TabsList data-testid="trip-tabs" className="inline-flex min-w-max">
            <TabLink value="datos" testid="tab-datos" icon={Pencil} label="Datos" tripId={tripId} setActiveTab={setActiveTab} />
            {/* Tab Contabilidad visible para todos; el módulo interno "Rentabilidad Viajes" mantiene su propio gate. */}
            <TabLink value="contabilidad" testid="tab-contabilidad" icon={Sigma} label="Contabilidad" tripId={tripId} setActiveTab={setActiveTab} />
            <TabLink value="reservas" testid="tab-reservas" icon={ListChecks} label={`Reservas (${reservas.length})`} tripId={tripId} setActiveTab={setActiveTab} />
            <TabLink value="recordatorios" testid="tab-pendientes" icon={ListTodo} label="Pendientes" tripId={tripId} setActiveTab={setActiveTab} badge={pendientes > 0 ? pendientes : null} badgeTestid="pendientes-badge" />
            {sold && <TabLink value="asistente" testid="tab-asistente" icon={Mailbox} label="Asistente" tripId={tripId} setActiveTab={setActiveTab} />}
            {sold && !kk && <TabLink value="reconfirmaciones" testid="tab-reconfirmaciones" icon={CalendarCheck} label="Reconfirmaciones" tripId={tripId} setActiveTab={setActiveTab} />}
            {sold && !kk && <TabLink value="traveldoc" testid="tab-traveldoc" icon={FileText} label="Travel Document" tripId={tripId} setActiveTab={setActiveTab} />}
            {sold && <TabLink value="kimkim" testid="tab-kimkim" icon={CalendarCheck} label="KimKim" tripId={tripId} setActiveTab={setActiveTab} />}
          </TabsList>
        </div>
        <TabsContent value="datos" className="mt-4"><DatosTab trip={trip} tripId={tripId} travelers={travelers} /></TabsContent>
        <TabsContent value="reservas" className="mt-4"><ReservasTab tripId={tripId} reservas={reservas} trip={tripWithTitular} recordatorios={recordatorios || []} /></TabsContent>
        {sold && <TabsContent value="asistente" className="mt-4"><AsistenteTab tripId={tripId} /></TabsContent>}
        {sold && !kk && <TabsContent value="reconfirmaciones" className="mt-4"><ReconfirmacionesTab tripId={tripId} /></TabsContent>}
        {sold && !kk && <TabsContent value="traveldoc" className="mt-4"><TravelDocTab tripId={tripId} trip={tripWithTitular} /></TabsContent>}
        {sold && <TabsContent value="kimkim" className="mt-4"><KimKimTab tripId={tripId} trip={trip} /></TabsContent>}
        <TabsContent value="recordatorios" className="mt-4"><RecordatoriosTab tripId={tripId} recordatorios={recordatorios || []} reservas={reservas} /></TabsContent>
        <TabsContent value="contabilidad" className="mt-4"><ContabilidadTab accounting={accounting} trip={trip} canSeeRentabilidad={canSeeRentabilidad} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ----------------------------- Datos generales -----------------------------
// Calcula el nº de noches entre 2 fechas ISO (YYYY-MM-DD). Devuelve "—" si falta alguna.
function calcNights(start, end) {
  if (!start || !end) return "—";
  const d1 = new Date(start); const d2 = new Date(end);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return "—";
  const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff : "—";
}

function DatosTab({ trip, tripId, travelers }) {
  const qc = useQueryClient();
  // Inicializa vuelo llegada/salida: si trip tiene los nuevos campos usarlos,
  // si no volcamos el legacy flight_details en "arrival" para no perder datos.
  const initArrival = trip.flight_arrival ?? (trip.flight_arrival == null && !trip.flight_departure ? (trip.flight_details || "") : "");
  const initDeparture = trip.flight_departure || "";
  const initial = {
    ...trip,
    fx_rate: trip.fx_rate ?? "",
    adults: trip.adults ?? Math.max(0, (trip.number_of_travelers || 1) - (trip.number_of_children || 0)),
    flight_arrival: initArrival,
    flight_departure: initDeparture,
    start_date: trip.start_date ? String(trip.start_date).slice(0, 10) : "",
    end_date: trip.end_date ? String(trip.end_date).slice(0, 10) : "",
  };
  const [form, setForm] = useState(initial);
  // `dirty` = true si el usuario cambió algo respecto al valor persistido.
  const [dirty, setDirty] = useState(false);
  const set = (k, v) => { setForm((s) => ({ ...s, [k]: v })); setDirty(true); };
  const saveMut = useMutation({
    mutationFn: () => api.put(`/trips/${trip.id}`, {
      main_traveler: form.main_traveler, agent: form.agent, ag_operaciones: form.ag_operaciones, traveler_support: form.traveler_support, phone: form.phone, destination: form.destination,
      source: form.source, partner: form.partner, status: form.status, booking_date: form.booking_date || null,
      start_date: form.start_date || null, end_date: form.end_date || null,
      adults: parseInt(form.adults) || 0,
      number_of_children: parseInt(form.number_of_children) || 0,
      children_ages: form.children_ages || "",
      flight_arrival: form.flight_arrival || "", flight_departure: form.flight_departure || "",
      notes: form.notes, fx_rate: form.fx_rate === "" ? null : parseFloat(form.fx_rate),
      number_of_travelers: parseInt(form.number_of_travelers) || 1,
    }),
    onSuccess: () => {
      toast.success("Viaje actualizado");
      setDirty(false);
      // Refresca el panel resumen superior + listado de viajes.
      qc.invalidateQueries({ queryKey: ["trip", trip.id] });
      qc.invalidateQueries({ queryKey: ["trips"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const teamQ = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/users/team")).data });
  const team = teamQ.data?.items || [];

  // Roles permitidos por selector · petición Eduardo (2026-02-28):
  //   Ag. Ventas   → travel_specialist + director_sales (Beatriz) + dir_operaciones (Marina)
  //   Reservas     → agente_reservas (Heidi) + los mismos que Ag. Ventas
  //   Support      → traveler_support (Nadia, Daniela)
  const ROLES_BY_FIELD = {
    agent: new Set(["travel_specialist", "director_sales", "dir_operaciones"]),
    ag_operaciones: new Set(["agente_reservas", "travel_specialist", "director_sales", "dir_operaciones"]),
    traveler_support: new Set(["traveler_support"]),
  };

  const agentSelect = (field, testid) => {
    const allowed = ROLES_BY_FIELD[field] || null;
    const filteredTeam = allowed ? team.filter((m) => allowed.has(m.role)) : team;
    return (
      <Select value={form[field] ? form[field] : "__none__"} onValueChange={(v) => set(field, v === "__none__" ? "" : v)}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Sin asignar" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— Sin asignar —</SelectItem>
          {form[field] && !filteredTeam.some((m) => m.full_name === form[field]) ? <SelectItem value={form[field]}>{form[field]} (actual)</SelectItem> : null}
          {/* Solo el nombre (Ventas, oct-2026): sin «· Directora de Operaciones». */}
          {filteredTeam.map((m) => <SelectItem key={m.id} value={m.full_name}>{m.full_name}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  };

  return (
    <div className="space-y-5 pb-24">
      <FloatingSaveButton
        onClick={() => saveMut.mutate()}
        saving={saveMut.isPending}
        disabled={!dirty}
        label="Guardar cambios"
        testid="save-datos-button"
      />
      <Card>
      <CardContent className="grid grid-cols-1 gap-x-4 gap-y-4 p-6 sm:grid-cols-12 sm:items-start">
        {/* Fila 1 · Cliente · Estado · Equipo asignado (Tel. eliminado 03-ago-2026:
            ya se muestra arriba en el resumen desde el viajero principal). */}
        <div className="space-y-1.5 sm:col-span-4"><Label>Cliente (Lead)</Label><Input value={form.main_traveler || ""} onChange={(e) => set("main_traveler", e.target.value)} data-testid="datos-name" /></div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Estado</Label>
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="datos-status"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(TRIP_STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Ag. Ventas</Label>{agentSelect("agent", "datos-agent")}</div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Reservas</Label>{agentSelect("ag_operaciones", "datos-ag-operaciones")}</div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Support</Label>{agentSelect("traveler_support", "datos-traveler-support")}</div>

        {/* Fila 2 · Destino · Fechas · Noches · Pax */}
        <div className="space-y-1.5 sm:col-span-3"><Label>Destino</Label><DestinationMultiSelect value={form.destination || ""} onChange={(v) => set("destination", v)} testid="datos-destination" /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Start</Label><Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="datos-start-date" /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Finish</Label><Input type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} data-testid="datos-end-date" /></div>
        <div className="space-y-1.5 sm:col-span-1"><Label>Nights</Label><Input readOnly disabled className="bg-muted/40 tabular-nums text-center" value={calcNights(form.start_date, form.end_date)} data-testid="datos-nights" /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Adultos</Label><Input type="number" min="0" value={form.adults ?? 1} onChange={(e) => set("adults", e.target.value)} data-testid="datos-adults" /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Niños</Label><Input type="number" min="0" value={form.number_of_children ?? 0} onChange={(e) => set("number_of_children", e.target.value)} data-testid="datos-children" /></div>
        {parseInt(form.number_of_children) > 0 && (
          <div className="space-y-1.5 sm:col-span-12"><Label>Edades de los niños</Label><Input value={form.children_ages || ""} onChange={(e) => set("children_ages", e.target.value)} placeholder="5, 8, 12" data-testid="datos-children-ages" /></div>
        )}

        {/* Fila 3 · Vuelos + Fuente + Fecha Venta (items-start alinea los inputs 1-line con los textareas por arriba) */}
        <div className="space-y-1.5 sm:col-span-4"><Label>Vuelo de llegada</Label><Textarea value={form.flight_arrival || ""} onChange={(e) => set("flight_arrival", e.target.value)} rows={4} placeholder="Aerolínea, nº vuelo, hora, aeropuerto…" data-testid="datos-flight-arrival" /></div>
        <div className="space-y-1.5 sm:col-span-4"><Label>Vuelo de salida</Label><Textarea value={form.flight_departure || ""} onChange={(e) => set("flight_departure", e.target.value)} rows={4} placeholder="Aerolínea, nº vuelo, hora, aeropuerto…" data-testid="datos-flight-departure" /></div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Fuente (Source)</Label>
          <Select value={form.source || "__none__"} onValueChange={(v) => set("source", v === "__none__" ? "" : v)}>
            <SelectTrigger data-testid="datos-source"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Sin asignar —</SelectItem>
              {form.source && !SOURCES.includes(form.source) ? <SelectItem value={form.source}>{form.source} (actual)</SelectItem> : null}
              {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Fecha de venta</Label><Input type="date" value={form.booking_date ? String(form.booking_date).slice(0, 10) : ""} onChange={(e) => set("booking_date", e.target.value)} data-testid="datos-booking-date" /></div>

        {/* Fila 4 · Notas en ancho completo */}
        <div className="space-y-1.5 sm:col-span-12"><Label>Notas</Label><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={3} /></div>

        <div className="sm:col-span-12 flex items-center gap-3 pt-2">
          {dirty && !saveMut.isPending && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold text-amber-700" data-testid="datos-dirty-badge">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
              Cambios sin guardar — usa el botón Guardar flotante o Ctrl+S
            </span>
          )}
        </div>
      </CardContent>
      </Card>

      {/* Sección Viajeros — antes era un tab independiente, ahora vive al pie de Datos */}
      <div>
        <div className="mb-2 flex items-center gap-2 px-1">
          <Users className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-muted-foreground">Viajeros ({(travelers || []).length})</h3>
        </div>
        <ViajerosTab tripId={tripId} travelers={travelers || []} tripEnd={trip.end_date} />
      </div>
    </div>
  );
}

// ----------------------------- Viajeros -----------------------------
const EMPTY_TRAVELER = { first_name: "", last_name: "", date_of_birth: "", nationality: "", passport_number: "", passport_expiry: "", email: "", phone: "", special_requests_comments: "", client: false };

// Fechas ISO (YYYY-MM-DD) → dd/mm/aaaa, parseadas como fecha local (sin desfase por zona horaria).
const parseLocalDate = (iso) => { const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number); return y && m && d ? new Date(y, m - 1, d) : null; };
const fmtDob = (iso) => { const dt = iso ? parseLocalDate(iso) : null; return dt ? dt.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }) : ""; };
const passportExpiryState = (iso, tripEnd) => {
  if (!iso) return null;
  const exp = parseLocalDate(iso);
  if (!exp || Number.isNaN(exp.getTime())) return null;
  const ref = (tripEnd && parseLocalDate(tripEnd)) || new Date();
  const months6 = new Date(ref); months6.setMonth(months6.getMonth() + 6);
  if (exp < ref) return "expired";
  if (exp < months6) return "soon";
  return "ok";
};

function ViajerosTab({ tripId, travelers, tripEnd }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_TRAVELER);
  const [delTarget, setDelTarget] = useState(null);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const invalidate = () => qc.invalidateQueries({ queryKey: ["trip", tripId] });

  const saveMut = useMutation({
    mutationFn: () => editing ? api.put(`/trips/${tripId}/travelers/${editing.id}`, form) : api.post(`/trips/${tripId}/travelers`, form),
    onSuccess: () => { toast.success(editing ? "Viajero actualizado" : "Viajero añadido"); invalidate(); setOpen(false); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/trips/${tripId}/travelers/${id}`),
    onSuccess: () => { toast.success("Viajero eliminado"); invalidate(); setDelTarget(null); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => { setEditing(null); setForm(EMPTY_TRAVELER); setOpen(true); }} data-testid="new-traveler-button"><Plus className="mr-1.5 h-4 w-4" />Añadir viajero</Button>
      </div>
      <Card><CardContent className="p-0">
        {travelers.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="travelers-empty">Sin viajeros todavía.</div>
        ) : (
          <Table data-testid="travelers-table">
            <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Nacimiento</TableHead><TableHead>Nacionalidad</TableHead><TableHead>Pasaporte</TableHead><TableHead>Exp Pasaporte</TableHead><TableHead>Email</TableHead><TableHead>Peticiones / restricciones</TableHead><TableHead>Cliente</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
            <TableBody>
              {travelers.map((t) => {
                const exp = passportExpiryState(t.passport_expiry, tripEnd);
                return (
                <TableRow key={t.id} data-testid={`traveler-row-${t.id}`}>
                  <TableCell className="font-medium">{t.first_name} {t.last_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground tabular-nums" data-testid={`traveler-dob-${t.id}`}>{fmtDob(t.date_of_birth) || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.nationality || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.passport_number || "—"}</TableCell>
                  <TableCell className="text-sm tabular-nums" data-testid={`traveler-passport-expiry-${t.id}`}>
                    {t.passport_expiry ? (
                      <span className={`rounded px-1.5 py-0.5 font-medium ${exp === "expired" ? "bg-red-100 text-red-700" : exp === "soon" ? "bg-amber-100 text-amber-800" : "text-muted-foreground"}`} title={exp === "expired" ? "Pasaporte caducado" : exp === "soon" ? "Caduca en menos de 6 meses tras el viaje" : "Caducidad del pasaporte"}>
                        {fmtDob(t.passport_expiry)}
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.email || "—"}</TableCell>
                  <TableCell className="max-w-[260px] text-sm" data-testid={`traveler-requests-${t.id}`}>
                    {t.special_requests_comments
                      ? <span className="line-clamp-2 whitespace-pre-line rounded bg-amber-50 px-1.5 py-0.5 text-amber-900" title={t.special_requests_comments}>{t.special_requests_comments}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>{t.client ? <Badge className="text-[10px]">Cliente</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(t); setForm({ ...EMPTY_TRAVELER, ...t, date_of_birth: (t.date_of_birth || "").slice(0, 10), passport_expiry: (t.passport_expiry || "").slice(0, 10) }); setOpen(true); }} data-testid={`edit-traveler-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => setDelTarget(t)} data-testid={`delete-traveler-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="traveler-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar viajero" : "Nuevo viajero"}</DialogTitle><DialogDescription>Datos del pasajero.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Nombre *</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="traveler-firstname" /></div>
            <div className="space-y-1.5"><Label>Apellidos</Label><Input value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Fecha de nacimiento</Label><Input type="date" value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="traveler-dob" /><p className="text-[10px] text-muted-foreground">Si cumple años durante el viaje, Ventas y Support reciben un aviso ese día.</p></div>
            <div className="space-y-1.5"><Label>Nacionalidad</Label><Input value={form.nationality || ""} onChange={(e) => set("nationality", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Pasaporte</Label><Input value={form.passport_number || ""} onChange={(e) => set("passport_number", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Caducidad del pasaporte</Label><Input type="date" value={form.passport_expiry || ""} onChange={(e) => set("passport_expiry", e.target.value)} data-testid="traveler-passport-expiry" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Teléfono</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Peticiones / restricciones</Label><Textarea value={form.special_requests_comments || ""} onChange={(e) => set("special_requests_comments", e.target.value)} /></div>
            <div className="flex items-center gap-2 sm:col-span-2"><Switch checked={!!form.client} onCheckedChange={(v) => set("client", v)} data-testid="traveler-client-switch" /><Label>Es el cliente/contacto</Label></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.first_name || saveMut.isPending} data-testid="save-traveler-button">{saveMut.isPending ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar viajero?</AlertDialogTitle><AlertDialogDescription>Se eliminará “{delTarget?.first_name} {delTarget?.last_name}”.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => delMut.mutate(delTarget.id)} data-testid="confirm-delete-traveler">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ----------------------------- Reservas -----------------------------
const EMPTY_RESERVA = { service: "", type: "actividad", city: "", country: "", provider_id: null, provider_name: "", date_entry: "", quantity: "", cost: "", currency: "EUR", date_exit: "", note: "" };

// Proveedor con autocompletado: al escribir aparece el listado de proveedores para elegir el correcto.
function ProviderSearchInput({ value, onPick, onText, testid = "reserva-provider" }) {
  const [term, setTerm] = useState(value || "");
  const [q, setQ] = useState("");
  const [openList, setOpenList] = useState(false);
  useEffect(() => { const t = setTimeout(() => setQ(term), 250); return () => clearTimeout(t); }, [term]);
  const { data } = useQuery({
    queryKey: ["provider-search-new-reserva", q],
    queryFn: async () => (await api.get("/providers", { params: { q, limit: 15 } })).data,
    enabled: openList && q.trim().length >= 1,
    keepPreviousData: true,
  });
  const items = data?.items || [];
  return (
    <div className="relative">
      <Input
        value={term}
        onChange={(e) => { setTerm(e.target.value); onText(e.target.value); setOpenList(true); }}
        onFocus={() => setOpenList(true)}
        onBlur={() => setTimeout(() => setOpenList(false), 150)}
        placeholder="Escribe para buscar…"
        autoComplete="off"
        data-testid={testid}
      />
      {openList && q.trim().length >= 1 && (
        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md" data-testid={`${testid}-list`}>
          {items.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">Sin coincidencias · se guardará el texto tal cual</div>}
          {items.map((p) => {
            const name = p.nombre_comercial || p.name;
            return (
              <button
                key={p.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setTerm(name); onPick(p); setOpenList(false); }}
                className="flex w-full flex-col items-start rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                data-testid={`${testid}-option-${p.id}`}
              >
                <span className="font-medium">{name}</span>
                <span className="text-[11px] text-muted-foreground">{[p.sistema, p.ciudad || p.city, p.pais || p.country].filter(Boolean).join(" · ")}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReservasTab({ tripId, reservas, trip, recordatorios = [] }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_RESERVA);
  const [delTarget, setDelTarget] = useState(null);
  // URL como fuente de verdad de qué reserva está abierta (2026-08).
  // Permite que el operador tenga varias pestañas del navegador abiertas,
  // una por reserva, y que el clic-derecho «Abrir en pestaña nueva» sobre
  // una fila abra la reserva directamente en otra tab del navegador.
  const [searchParams, setSearchParams] = useSearchParams();
  const selReserva = searchParams.get("reserva") || null;
  // Guardamos el último id abierto para que al volver del detalle podamos
  // scrollear a esa fila en la lista (persiste al cerrar la reserva).
  const lastSelRef = useRef(null);
  useEffect(() => { if (selReserva) lastSelRef.current = selReserva; }, [selReserva]);
  const openReserva = (id) => {
    lastSelRef.current = id;
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("tab", "reservas");
      p.set("reserva", id);
      return p;
    }, { replace: false });
  };
  const closeReserva = () => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete("reserva");
      return p;
    }, { replace: false });
  };
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const invalidate = () => qc.invalidateQueries({ queryKey: ["trip", tripId] });

  const payload = () => {
    const cost = parseFloat(form.cost) || 0;
    const { cost: _c, ...rest } = form;
    return {
      ...rest,
      quantity: parseInt(form.quantity, 10) || parseInt(trip?.adults, 10) || 1,
      date_entry: form.date_entry || null,
      // Un único «Coste €» (Dirección, nov-2026): se guarda igual con y sin IVA.
      cost_tax_excl: cost,
      cost_tax_incl: cost,
    };
  };
  const saveMut = useMutation({
    mutationFn: () => editing ? api.put(`/trips/${tripId}/reservas/${editing.id}`, payload()) : api.post(`/trips/${tripId}/reservas`, payload()),
    onSuccess: () => { toast.success(editing ? "Reserva actualizada" : "Reserva añadida"); invalidate(); setOpen(false); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/trips/${tripId}/reservas/${id}`),
    onSuccess: () => { toast.success("Reserva eliminada"); invalidate(); setDelTarget(null); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const stSum = reservas.reduce((a, r) => { a[r.status_reserva] = (a[r.status_reserva] || 0) + 1; return a; }, {});
  const stTotal = reservas.filter((r) => r.status_reserva !== "cancelada").length;

  return (
    selReserva ? (
      <ReservaDetail
        tripId={tripId}
        trip={trip}
        reserva={reservas.find((r) => r.id === selReserva) || {}}
        recordatorios={recordatorios}
        onBack={closeReserva}
        onChanged={invalidate}
      />
    ) : (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="reservas-status-summary">
          <span className="text-muted-foreground">Estados:</span>
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono" title="No contactado">N {stSum.sin_seleccion || 0}</span>
          <span className="rounded bg-sky-100 px-1.5 py-0.5 font-mono text-sky-700" title="Contactado">C {stSum.contacto || 0}</span>
          <span className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-amber-800" title="Confirmado">R {stSum.reserva || 0}</span>
          <span className="rounded bg-emerald-100 px-1.5 py-0.5 font-mono text-emerald-700" title="Re-confirmado">F {stSum.confirmacion || 0}</span>
          <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono font-bold text-primary" title="Total de reservas del trip">T {stTotal}</span>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setForm(EMPTY_RESERVA); setOpen(true); }} data-testid="new-reserva-button"><Plus className="mr-1.5 h-4 w-4" />Añadir reserva</Button>
      </div>
      {reservas.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="reservas-empty">Sin reservas todavía.</CardContent></Card>
      ) : (
        <ReservasDayList tripId={tripId} trip={trip} reservas={reservas} onOpen={openReserva} onDelete={setDelTarget} lastOpenedId={lastSelRef.current} />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="reserva-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar reserva" : "Nueva reserva"}</DialogTitle><DialogDescription>Servicio del viaje: proveedor, fecha, pax y coste.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Servicio *</Label><Input value={form.service} onChange={(e) => set("service", e.target.value)} data-testid="reserva-service" /></div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="reserva-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(RESERVA_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Proveedor</Label>
              <ProviderSearchInput
                value={form.provider_name}
                onText={(v) => setForm((s) => ({ ...s, provider_name: v, provider_id: null }))}
                onPick={(p) => setForm((s) => ({ ...s, provider_id: p.id, provider_name: p.nombre_comercial || p.name, country: s.country || p.pais || p.country || "", city: s.city || p.ciudad || p.city || "" }))}
              />
            </div>
            <div className="space-y-1.5"><Label>Fecha de servicio</Label><Input type="date" value={form.date_entry || ""} onChange={(e) => set("date_entry", e.target.value)} data-testid="reserva-date" /></div>
            <div className="space-y-1.5"><Label>Pax</Label><Input type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder={trip?.adults ? `Viaje: ${trip.adults}` : "1"} data-testid="reserva-pax" /></div>
            <div className="space-y-1.5"><Label>Ciudad</Label><Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="reserva-city" /></div>
            <div className="space-y-1.5"><Label>País</Label><Input value={form.country || ""} onChange={(e) => set("country", e.target.value)} placeholder="ES, FR…" data-testid="reserva-country" /></div>
            <div className="space-y-1.5"><Label>Coste €</Label><Input type="number" step="0.01" value={form.cost} onChange={(e) => set("cost", e.target.value)} placeholder="Coste total del servicio" data-testid="reserva-cost" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Notas</Label><Textarea value={form.note || ""} onChange={(e) => set("note", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.service || saveMut.isPending} data-testid="save-reserva-button">{saveMut.isPending ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar reserva?</AlertDialogTitle><AlertDialogDescription>Se eliminará “{delTarget?.service}”.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => delMut.mutate(delTarget.id)} data-testid="confirm-delete-reserva">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    )
  );
}

// ----------------------------- Recordatorios -----------------------------
const EMPTY_REC = { note: "", to: "", fecha_recordatorio: "", reserva_id: "", enviar_correo: false };

function RecordatoriosTab({ tripId, recordatorios, reservas }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_REC);
  const [delTarget, setDelTarget] = useState(null);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const invalidate = () => qc.invalidateQueries({ queryKey: ["trip", tripId] });
  const payload = () => ({ ...form, reserva_id: form.reserva_id || null });

  const saveMut = useMutation({
    mutationFn: () => editing ? api.put(`/trips/${tripId}/recordatorios/${editing.id}`, payload()) : api.post(`/trips/${tripId}/recordatorios`, payload()),
    onSuccess: () => { toast.success(editing ? "Recordatorio actualizado" : "Recordatorio añadido"); invalidate(); setOpen(false); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const resolveMut = useMutation({
    mutationFn: (id) => api.patch(`/trips/${tripId}/recordatorios/${id}/resolve`),
    onSuccess: () => { invalidate(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/trips/${tripId}/recordatorios/${id}`),
    onSuccess: () => { toast.success("Recordatorio eliminado"); invalidate(); setDelTarget(null); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const reservaName = (id) => (reservas.find((r) => r.id === id) || {}).service;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">El envío por email se activa en la Iteración 9 (Asistente de Reservas); aquí se registra la intención.</p>
        <Button size="sm" onClick={() => { setEditing(null); setForm(EMPTY_REC); setOpen(true); }} data-testid="new-recordatorio-button"><Plus className="mr-1.5 h-4 w-4" />Nuevo recordatorio</Button>
      </div>
      <Card><CardContent className="p-0">
        {recordatorios.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="recordatorios-empty">Sin recordatorios todavía.</div>
        ) : (
          <Table data-testid="recordatorios-table">
            <TableHeader><TableRow><TableHead className="w-10"></TableHead><TableHead>Recordatorio</TableHead><TableHead>Para</TableHead><TableHead>Reserva</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
            <TableBody>
              {recordatorios.map((r) => (
                <TableRow key={r.id} className={r.solved ? "opacity-60" : ""} data-testid={`recordatorio-row-${r.id}`}>
                  <TableCell>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => resolveMut.mutate(r.id)} data-testid={`resolve-recordatorio-${r.id}`} aria-label="Resolver">
                      {r.solved ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                  </TableCell>
                  <TableCell className={`max-w-[280px] font-medium ${r.solved ? "line-through" : ""}`}>
                    {r.note}
                    {r.enviar_correo && <Mail className="ml-1.5 inline h-3.5 w-3.5 text-sky-600" />}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.to || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.reserva_id ? (reservaName(r.reserva_id) || "—") : "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(r.fecha_recordatorio)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(r); setForm({ ...EMPTY_REC, ...r, reserva_id: r.reserva_id || "", fecha_recordatorio: r.fecha_recordatorio || "" }); setOpen(true); }} data-testid={`edit-recordatorio-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => setDelTarget(r)} data-testid={`delete-recordatorio-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="recordatorio-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar recordatorio" : "Nuevo recordatorio"}</DialogTitle><DialogDescription>Aviso ligado al viaje (o a una reserva concreta).</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Recordatorio *</Label><Textarea value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="recordatorio-note" /></div>
            <div className="space-y-1.5"><Label>Para (destinatario)</Label><Input value={form.to || ""} onChange={(e) => set("to", e.target.value)} placeholder="reservas@sofi.com" data-testid="recordatorio-to" /></div>
            <div className="space-y-1.5"><Label>Fecha</Label><Input type="date" value={form.fecha_recordatorio || ""} onChange={(e) => set("fecha_recordatorio", e.target.value)} data-testid="recordatorio-fecha" /></div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Reserva (opcional)</Label>
              <Select value={form.reserva_id || "none"} onValueChange={(v) => set("reserva_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="recordatorio-reserva"><SelectValue placeholder="Ligar a una reserva" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Solo el viaje —</SelectItem>
                  {reservas.map((r) => <SelectItem key={r.id} value={r.id}>{r.service}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 sm:col-span-2"><Switch checked={!!form.enviar_correo} onCheckedChange={(v) => set("enviar_correo", v)} data-testid="recordatorio-email-switch" /><Label>Avisar por email <span className="text-xs text-muted-foreground">(envío real: It.9)</span></Label></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.note || saveMut.isPending} data-testid="save-recordatorio-button">{saveMut.isPending ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar recordatorio?</AlertDialogTitle><AlertDialogDescription>Se eliminará este recordatorio.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => delMut.mutate(delTarget.id)} data-testid="confirm-delete-recordatorio">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ----------------------------- Contabilidad en vivo -----------------------------
function ContabilidadTab({ accounting, trip, canSeeRentabilidad = false }) {
  const qc = useQueryClient();
  const mk = accounting?.markup || {};
  const bk = accounting?.book || {};
  const stat = accounting?.status_summary || {};
  const lines = accounting?.result?.lines || [];
  const rb = accounting?.rentabilidad || {};

  // Refresco manual del Pricing Summary KimKim (rescrapea /markup-itemized-pricing
  // y actualiza los "Due to Specialists" + totales). Toast con nº de campos que cambian.
  const kkPricingRefreshMut = useMutation({
    mutationFn: async () => (await api.post(`/trips/${trip.id}/kimkim/refresh-pricing-summary`)).data,
    onSuccess: (data) => {
      const n = (data?.changed_fields || []).length;
      toast.success(n === 0
        ? "Pricing KimKim al día — nada ha cambiado"
        : `Pricing KimKim actualizado · ${n} campo${n === 1 ? "" : "s"} cambiaron`);
      qc.invalidateQueries({ queryKey: ["trip", trip.id] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const [form, setForm] = useState({
    fx_rate: trip.fx_rate ?? "",
    commission_pct: trip.commission_pct ?? "",
    partner: trip.partner || "other",
    final_price_amount: trip.final_price_amount ?? "",
    partner_margin_usd: trip.partner_margin_usd ?? "",
    donation_usd: trip.donation_usd ?? "",
    partner_commission_pct: trip.partner_commission_pct ?? "",
    paypal_fee: !!trip.paypal_fee,
    sold_in_eur: !!trip.sold_in_eur,
    // KimKim: Precio final (viajero) € y Exchange rate, prepoblados desde el Pricing Summary sincronizado.
    kk_final_price_traveler_eur: trip.kk_final_price_traveler_eur ?? trip.kimkim?.kk_final_price_eur ?? trip.kimkim_pricing_summary?.trip_total_eur ?? "",
    kk_exchange_rate: trip.kk_exchange_rate ?? (accounting?.markup?.kk_exchange_rate || trip.fx_rate || ""),
    // Ingresado: para partner=kimkim autopoblar con el "Ingresado total (KimKim → nosotros)"
    // si aún está vacío; para otros partners, mantener el valor manual.
    income_amount: (() => {
      if (trip.income_amount != null && trip.income_amount !== "" && trip.income_amount !== 0) return trip.income_amount;
      if (trip.partner === "kimkim" && trip.kimkim?.kk_ingresado_total_eur != null) return trip.kimkim.kk_ingresado_total_eur;
      return trip.income_amount ?? "";
    })(),
    refunded_usd: trip.refunded_usd ?? "",
    accounting_notes: trip.accounting_notes || "",
    adelanto: trip.adelanto ?? "",
    fact_adelanto: trip.fact_adelanto || "",
    fact_final: trip.fact_final || "",
    ajuste: trip.ajuste ?? "",
    pago: trip.pago || "inicial",
    rentabilidad_notas: trip.rentabilidad_notas || "",
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const num = (v) => (v === "" || v == null ? null : parseFloat(v));
  const otherCur = form.sold_in_eur ? "$" : "€";

  const saveMut = useMutation({
    mutationFn: () => api.put(`/trips/${trip.id}`, {
      fx_rate: num(form.fx_rate),
      commission_pct: num(form.commission_pct) ?? 0,
      partner: form.partner,
      final_price_amount: num(form.final_price_amount) ?? 0,
      partner_margin_usd: num(form.partner_margin_usd) ?? 0,
      donation_usd: num(form.donation_usd) ?? 0,
      partner_commission_pct: num(form.partner_commission_pct) ?? 0,
      paypal_fee: form.paypal_fee,
      sold_in_eur: form.sold_in_eur,
      kk_final_price_traveler_eur: num(form.kk_final_price_traveler_eur),
      kk_exchange_rate: num(form.kk_exchange_rate),
      income_amount: num(form.income_amount) ?? 0,
      refunded_usd: num(form.refunded_usd) ?? 0,
      accounting_notes: form.accounting_notes,
      adelanto: num(form.adelanto) ?? 0,
      fact_adelanto: form.fact_adelanto,
      fact_final: form.fact_final,
      ajuste: num(form.ajuste) ?? 0,
      pago: form.pago,
      rentabilidad_notas: form.rentabilidad_notas,
    }),
    onSuccess: () => { toast.success("Contabilidad guardada"); qc.invalidateQueries({ queryKey: ["trip", trip.id] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const RO = ({ label, value, testid, strong, hint, tone }) => (
    <div data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}{hint && <span title={hint} className="ml-1 cursor-help opacity-60">ⓘ</span>}</p>
      <p className={`tabular-nums ${strong ? "text-xl font-bold text-primary" : "text-base font-semibold"} ${tone === "pos" ? "text-emerald-600" : tone === "neg" ? "text-rose-600" : ""}`}>{value}</p>
    </div>
  );
  const NumIn = ({ label, k, step = "0.01", testid, hint }) => (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}{hint && <span title={hint} className="ml-1 cursor-help opacity-60">ⓘ</span>}</Label>
      <Input type="number" step={step} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={testid} />
    </div>
  );

  return (
    <div className="space-y-4 pb-24" data-testid="contabilidad-tab">
      <FloatingSaveButton
        onClick={() => saveMut.mutate()}
        saving={saveMut.isPending}
        label="Guardar contabilidad"
        testid="save-accounting"
      />
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <span>Fórmula activa: <strong className="text-foreground">{accounting?.formula_version_label || `v${accounting?.formula_version}`}</strong></span>
        <span>· Estado reservas: <strong className="font-mono text-foreground">{stat.label || "—"}</strong></span>
      </div>

      {/* ===== MÓDULO 1 · COTIZACIÓN VENDIDA · Variante KimKim =====
          Renderiza SIEMPRE que el partner sea KimKim, incluso si aún no se ha
          hecho el scraping del Pricing Summary. Así el operador tiene el módulo
          visible desde el minuto 0 con placeholders "—" y el botón "Actualizar
          Pricing KimKim" a mano para poblarlo. Antes se ocultaba cuando no
          había datos → bug reportado por Eduardo (2026-07). */}
      {form.partner === "kimkim" && (
        <Card className="border-primary/30 bg-primary/[0.03]" data-testid="kk-totals-card">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Send className="h-4 w-4 text-primary" />
                Cotización vendida
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">Origen: KimKim</span>
                {trip.kimkim_code ? <span className="text-xs font-normal text-muted-foreground">· {trip.kimkim_code}</span> : null}
              </CardTitle>
              <div className="ml-auto flex flex-wrap items-center gap-1.5">
                {trip.kimkim_trip_url && (
                  <a
                    href={trip.kimkim_trip_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="kk-open-original"
                    className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2.5 py-1 text-xs font-medium hover:bg-accent"
                    title="Abrir el trip plan original en KimKim"
                  >
                    <ExternalLink className="h-3 w-3" />Abrir en KimKim
                  </a>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => kkPricingRefreshMut.mutate()}
                  disabled={kkPricingRefreshMut.isPending}
                  data-testid="kk-refresh-pricing-btn"
                  title="Re-scrapea el Pricing Summary de KimKim y actualiza el snapshot en el trip"
                  className="h-7 gap-1 text-xs"
                >
                  <RefreshCw className={`h-3 w-3 ${kkPricingRefreshMut.isPending ? "animate-spin" : ""}`} />
                  {kkPricingRefreshMut.isPending ? "Refrescando…" : "Actualizar Pricing KimKim"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-2 items-start gap-x-6 gap-y-4 sm:grid-cols-6">
            <NumIn label="Precio final (viajero) €" k="kk_final_price_traveler_eur" testid="kk-final-price-eur" />
            <NumIn label="Exchange rate" k="kk_exchange_rate" step="0.0001" testid="kk-exchange-rate" />
            <RO label="Precio final (viajero) $" value={fmtUSD(mk.kk_final_price_traveler_usd)} testid="kk-final-price-usd" strong hint="Precio final (viajero) € × exchange rate" />
            <RO label="Ingresado neto" value={fmtEUR(bk.income_eur)} testid="kk-ingresado-total" tone="pos" strong hint="Due to Specialists total (KimKim itemized pricing → Espíritu, neto)" />
            <RO label="Margen agente" value={trip.kimkim?.kk_markup_pct != null ? `${trip.kimkim.kk_markup_pct}%` : "—"} testid="kk-markup-pct" />
            <RO label="Comisión KimKim" value={fmtEUR(trip.kimkim?.kk_kimkim_commission_eur)} testid="kk-commission" hint="kimkim commission + service fee + carbon removal (informativo)" />
            <RO
              label="Booked by KimKim"
              value={fmtEUR(trip.kimkim_pricing_summary?.pagado_directo_kk_eur)}
              testid="kk-booked-by-kk"
              hint="Hoteles / actividades que KimKim gestiona y cobra directamente al viajero (no llegan a Espíritu)"
            />
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Trip vendido en €</p>
              <div className="mt-1 flex h-9 items-center gap-2">
                <Switch checked={form.sold_in_eur} onCheckedChange={(v) => set("sold_in_eur", v)} data-testid="kk-sold-eur" />
                <span className="text-sm">{form.sold_in_eur ? "Sí" : "No"}</span>
              </div>
            </div>
          </CardContent>
          {trip.kimkim_pricing_synced_at && (
            <div className="border-t border-primary/10 px-6 py-2 text-[10px] text-muted-foreground" data-testid="kk-pricing-synced-at">
              Snapshot actualizado el {new Date(trip.kimkim_pricing_synced_at).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" })}
            </div>
          )}
        </Card>
      )}

      {/* ===== MÓDULO 1 · COTIZACIÓN VENDIDA (SOFI 1.0 legacy — antes "Mark Up") =====
          Para partner=kimkim el módulo superior con el mismo nombre ya muestra los
          importes con datos de KimKim, por lo que ocultamos esta variante.
          Renombrada de "Mark Up" a "Cotización vendida" (2026-07, feedback Eduardo). */}
      {form.partner !== "kimkim" && (
      <Card>
        <CardHeader className="pb-2"><CardTitle className="flex flex-wrap items-center gap-2 text-base">
          Cotización vendida
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">Origen: Cotizador / manual</span>
        </CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <RO label="Cotizado Total (€)" value={fmtEUR(mk.cotizado_total_eur)} testid="mk-cotizado-eur" strong hint="Σ cotizado (s/IVA) de todas las reservas activas del viaje — igual que la columna Cotizado de la lista" />
          <RO label="Cotizado Total ($)" value={fmtUSD(mk.cotizado_total_usd)} testid="mk-cotizado-usd" hint="Cotizado Total (€) × cambio" />
          <NumIn label="Cambio € $" k="fx_rate" step="0.0001" testid="mk-fx" />
          <NumIn label="Comisión (%)" k="commission_pct" step="0.0001" testid="mk-commission" />
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Partner</Label>
            <Select value={form.partner} onValueChange={(v) => set("partner", v)}>
              <SelectTrigger data-testid="mk-partner"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PARTNER_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <NumIn label="Precio final ($)" k="final_price_amount" testid="mk-final-price" />
          <RO label="Precio final (€)" value={fmtEUR(mk.final_price_eur)} testid="mk-final-price-conv" hint="Precio final ÷ cambio (el cliente siempre paga en $)" />
          <NumIn label="Margen partner ($)" k="partner_margin_usd" testid="mk-partner-margin-in" />
          <NumIn label="Donation ($)" k="donation_usd" testid="mk-donation" />
          <NumIn label="Comisión Partner (%)" k="partner_commission_pct" step="0.0001" testid="mk-partner-commission" />
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">PayPal fee</Label>
            <div className="flex h-9 items-center"><Switch checked={form.paypal_fee} onCheckedChange={(v) => set("paypal_fee", v)} data-testid="mk-paypal" /><span className="ml-2 text-sm">{form.paypal_fee ? "Sí" : "No"}</span></div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Trip sold in €</Label>
            <div className="flex h-9 items-center"><Switch checked={form.sold_in_eur} onCheckedChange={(v) => set("sold_in_eur", v)} data-testid="mk-sold-eur" /><span className="ml-2 text-sm">{form.sold_in_eur ? "Sí" : "No"}</span></div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* ===== CONTABILIDAD VIAJE ===== */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Contabilidad Viaje</CardTitle></CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 items-start gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-6">
            {form.partner === "kimkim" ? (
              <>
                {bk.income_mode === "kimkim_due_to_specialists"
                  ? <RO label="Ingresado (€)" value={fmtEUR(bk.income_eur)} testid="bk-income" strong hint="Total «Due to Specialists» del itemized pricing de KimKim (sincronizado)" />
                  : <NumIn label="Ingresado (€)" k="income_amount" testid="bk-income" hint="Total «Due to Specialists» del itemized pricing de KimKim (introducir a mano o «Actualizar Pricing KimKim»)" />}
                <RO label="Ingresado ($)" value={fmtUSD(bk.income_usd)} testid="bk-income-usd" hint="Ingresado (€) × exchange rate" />
              </>
            ) : (
              <>
                <RO
                  label="Ingresado ($)"
                  value={fmtUSD(bk.income_usd)}
                  testid="bk-income-usd"
                  strong
                  hint={form.partner === "other"
                    ? `Precio final ($) − ${mk.paypal_fee_pct}% (${fmtUSD(mk.paypal_fee_usd)}) − Donation ($)`
                    : `Precio final ($) − Margen partner ($) − ${mk.paypal_fee_pct}% (${fmtUSD(mk.paypal_fee_usd)}) − Donation ($)`}
                />
                <RO label="Ingresado (€)" value={fmtEUR(bk.income_eur)} testid="bk-income" hint="Ingresado ($) ÷ cambio" />
              </>
            )}
            <RO
              label="Gastado (€)"
              value={fmtEUR(bk.total_gastado_excl_eur)}
              testid="bk-total-gastado"
              hint="Suma de la columna «Coste» (factura del proveedor sin IVA) de cada servicio de la lista de reservas"
            />
            <RO
              label="Reembolsado (€)"
              value={fmtEUR(bk.reembolsado_total_eur)}
              testid="bk-refunded"
              hint="Suma de la casilla «Reembolsado» de cada reserva del viaje"
            />
            <RO
              label="Total Gastado (€)"
              value={fmtEUR(bk.dif_gastado_reembolso_eur)}
              testid="bk-dif-gastado-reembolso"
              tone={(bk.dif_gastado_reembolso_eur || 0) >= 0 ? "pos" : "neg"}
              strong
              hint="Ingresado (€) − Reembolsado − Gastado"
            />
            <RO
              label="Provisión Contingencias (€)"
              value={fmtEUR(bk.provision_contingencias_eur)}
              testid="bk-provision-contingencias"
              tone={(bk.provision_contingencias_eur || 0) >= 0 ? "pos" : "neg"}
              hint="Cotizado Total (€) − Total Gastado (€)"
            />
          </div>
          <div className="mt-5 space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Notas</Label>
            <Textarea value={form.accounting_notes} onChange={(e) => set("accounting_notes", e.target.value)} rows={3} data-testid="bk-notes" />
          </div>
        </CardContent>
      </Card>

      {/* ═════════════════════════════════════════════════════════════════
          Módulos 3 y 4 — separados en 2026-07 (feedback Eduardo). Antes
          convivían dentro de "Rentabilidad Viajes". Ahora:
            · MÓDULO 3 · Rentabilidad ─ solo KPIs financieros (foto y realidad)
            · MÓDULO 4 · Facturación   ─ adelanto, pago final, ajuste, facturas
          Ambos con el mismo gate `canSeeRentabilidad` (visibilidad restringida).
         ═════════════════════════════════════════════════════════════════ */}

      {/* ===== MÓDULO 3 · RENTABILIDAD VIAJES (visibilidad restringida) ===== */}
      {canSeeRentabilidad && (
      <Card className="border-amber-300/50 bg-amber-50/30" data-testid="rentabilidad-card">
        <CardHeader className="pb-2"><CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <Lock className="h-4 w-4 text-amber-600" />Rentabilidad Viajes
          <span className="text-xs font-normal text-muted-foreground">· visible solo para dirección</span>
          {(() => {
            const closed = ["cerrado", "terminado", "viaje_empresa"].includes(trip.status);
            return (
              <span
                data-testid="rb-kpi-badge"
                className={`ml-auto rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${closed ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}
                title={closed ? "Viaje cerrado/terminado: el KPI es la Rentabilidad (coste real)" : "Viaje abierto: el KPI es el Margen de venta (foto al vender)"}
              >
                KPI: {closed ? "Rentabilidad" : "Margen de venta"}
              </span>
            );
          })()}
        </CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <RO label="Cotizado Total Venta ($)" value={fmtUSD(rb.cotizado_venta_usd)} testid="rb-cotizado-venta" hint={`= Cotizado Total ($) de la cotización vendida · ${fmtEUR(rb.cotizado_venta_eur)}`} />
          <RO label="Margen de venta ($)" value={fmtUSD(rb.margen_venta_usd)} testid="rb-margen-venta-usd" tone={(rb.margen_venta_usd || 0) >= 0 ? "pos" : "neg"} hint={`Ingresado ($) − Cotizado Total Venta ($) · ${fmtEUR(rb.margen_venta_eur)}`} />
          <RO label="Margen de venta (%)" value={rb.margen_venta_pct != null ? `${rb.margen_venta_pct}%` : "—"} testid="rb-margen-venta-pct" tone={(rb.margen_venta_pct || 0) >= 0 ? "pos" : "neg"} hint="Margen de venta ($) ÷ Precio final ($)" />
          <RO label="Rentabilidad ($)" value={fmtUSD(rb.margen_usd)} testid="rb-margen-usd" strong tone={(rb.margen_usd || 0) >= 0 ? "pos" : "neg"} hint={`Ingresado ($) − Gasto real ($) [factura del proveedor o, si no hay, coste cotizado] = ${fmtUSD(rb.ingresado_usd)} − ${fmtUSD(rb.gasto_real_usd)} · ${fmtEUR(rb.margen_eur)}`} />
          <RO label="Rentabilidad (%)" value={rb.margen_pct != null ? `${rb.margen_pct}%` : "—"} testid="rb-margen-pct" strong tone={(rb.margen_pct || 0) >= 0 ? "pos" : "neg"} hint="Rentabilidad ($) ÷ Precio final ($)" />
          <RO label="Coste Real (€)" value={fmtEUR(rb.coste_real_eur)} testid="rb-gastado-eur" hint="= Total Gastado (€) de Contabilidad Viaje (Ingresado − Reembolsado − Gastado)" />
          <RO label="Coste Real ($)" value={fmtUSD(rb.coste_real_usd)} testid="rb-gastado-usd" hint="Coste Real (€) × cambio" />
          <RO label="Mejora Op ($)" value={fmtUSD(rb.mejora_cotizacion_usd)} testid="rb-mejora-usd" strong tone={(rb.mejora_cotizacion_usd || 0) >= 0 ? "pos" : "neg"} hint={`Rentabilidad ($) − Margen de venta ($) · ${fmtEUR(rb.mejora_cotizacion_eur)}`} />
          <RO label="Mejora Op (%)" value={rb.mejora_cotizacion_pct != null ? `${rb.mejora_cotizacion_pct}%` : "—"} testid="rb-mejora-pct" tone={(rb.mejora_cotizacion_pct || 0) >= 0 ? "pos" : "neg"} hint="Mejora Op ($) ÷ Precio final ($)" />
          <div className="space-y-1.5 sm:col-span-3 lg:col-span-5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Notas rentabilidad</Label>
            <Textarea value={form.rentabilidad_notas} onChange={(e) => set("rentabilidad_notas", e.target.value)} rows={2} data-testid="rb-notas" />
          </div>
        </CardContent>
      </Card>
      )}

      {/* ===== MÓDULO 4 · FACTURACIÓN (visibilidad restringida) ===== */}
      {canSeeRentabilidad && (
      <Card className="border-sky-300/50 bg-sky-50/30" data-testid="facturacion-card">
        <CardHeader className="pb-2"><CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <Lock className="h-4 w-4 text-sky-600" />Facturación
          <span className="text-xs font-normal text-muted-foreground">· cobros, ajustes y referencias de factura</span>
        </CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <NumIn label="Adelanto ($)" k="adelanto" testid="fc-adelanto" />
          <RO label="Pago final ($)" value={fmtUSD(rb.pago_final)} testid="fc-pago-final" hint="Precio final − adelanto + ajuste (el cliente paga en $)" />
          <RO label="% sobre el total" value={rb.pct_sobre_total != null ? `${rb.pct_sobre_total}%` : "—"} testid="fc-pct-total" />
          <NumIn label="Ajuste ($)" k="ajuste" testid="fc-ajuste" />
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Pago</Label>
            <Select value={form.pago} onValueChange={(v) => set("pago", v)}>
              <SelectTrigger data-testid="fc-pago"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inicial">Inicial</SelectItem>
                <SelectItem value="final">Final</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Fact. Adelanto</Label>
            <Input value={form.fact_adelanto} onChange={(e) => set("fact_adelanto", e.target.value)} placeholder="ej. B/26/04" data-testid="fc-fact-adelanto" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Fact. Final</Label>
            <Input value={form.fact_final} onChange={(e) => set("fact_final", e.target.value)} placeholder="ej. B/26/12" data-testid="fc-fact-final" />
          </div>
        </CardContent>
      </Card>
      )}

      {/* ===== Desglose por servicio — OCULTO (2026-07, feedback Eduardo) =====
          El desglose línea a línea ya se ve en el Trip Detail y saturaba
          visualmente la tab de Contabilidad. Se mantiene el código por si
          hace falta reactivarlo desde una preferencia de usuario. */}
      {false && canSeeRentabilidad && (
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Desglose por servicio</CardTitle></CardHeader>
        <CardContent className="p-0">
          {lines.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Añade reservas para ver el cálculo.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="acc-lines-table">
                <TableHeader><TableRow><TableHead>Servicio</TableHead><TableHead>País</TableHead><TableHead className="text-right">Base</TableHead><TableHead className="text-right">Markup</TableHead><TableHead className="text-right">Uplift</TableHead><TableHead className="text-right">Cotizado línea</TableHead></TableRow></TableHeader>
                <TableBody>
                  {lines.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell className="max-w-[200px] truncate font-medium" title={l.description}>{l.description || "—"}</TableCell>
                      <TableCell>{l.is_spain ? <Badge variant="secondary" className="text-[10px]">ES · sin IVA</Badge> : <span className="text-xs text-muted-foreground">{l.country || "—"} · con IVA</span>}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtEUR(l.base_unit)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtEUR(l.markup_unit)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtEUR(l.uplift_unit)}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums">{fmtEUR(l.line_quoted)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
